'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, Divider, FormControl, InputLabel, Select, Typography } from '@mui/material';
import { ChangeEvent, FC, useState } from 'react';
import { useLocalStorage } from '@/hooks';
import { Category, Equipment, OfferTypeObject } from '@/types';
import { COLORS } from '@/constants';
import { ControlLabelWrapper, StyledCountData } from './styled';
import { useTranslations } from 'next-intl';
import { FilterAccordion } from '@/components';
import { StyledCheckbox, StyledFormControlLabel } from '../LandMultilevelCheckboxList/styles';
import { ArrowDown } from '@/components/icons/ArrowDown';

interface CheckboxGroupMenuProps {
  items: Array<Category | Equipment | OfferTypeObject>;
  value: Array<Category | Equipment | OfferTypeObject>;
  label: string;
  onChange: (items: any[]) => void;
  isSelect?: boolean;
  hideLabel?: boolean;
  hideCount?: boolean;
  width?: string | number;
  key?: string;
  disabled?: boolean;
  order?: string[][];
  defaultOpen?: boolean;
  hint?: string;
}

export const CheckboxGroupMenu: FC<CheckboxGroupMenuProps> = ({
  items,
  value,
  label,
  onChange,
  isSelect = false,
  hideLabel = false,
  hideCount = false,
  width = '200px',
  disabled,
  order,
  defaultOpen = true,
  hint,
}) => {
  const t = useTranslations();

  const [open, setOpen] = useState(false);
  const [showZeroGroups, setShowZeroGroups] = useLocalStorage<Record<number, boolean>>(
    `filter:showEmpty:${label}`,
    {},
  );

  const handleItemClick = (title: string) => (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const selectingItem = items.find((item) => item.title === title);
    const isValueChosen = value.find((item) => item.title === title);

    if (!isValueChosen && selectingItem) {
      onChange([...value, selectingItem]);
    }
    if (isValueChosen) {
      onChange(value.filter((item) => item.title !== title));
    }
  };

  const renderCheckbox = ({ title, count }: { title: string; count: number }) => (
    <ControlLabelWrapper key={title} onClick={(e) => e.stopPropagation()}>
      <StyledFormControlLabel
        sx={{ ml: 0 }}
        label={title}
        control={
          <StyledCheckbox
            checked={value.some((item) => item.title === title)}
            onChange={handleItemClick(title)}
            size="small"
            onClick={(e) => e.stopPropagation()}
          />
        }
      />
      {!hideCount && (
        <StyledCountData>
          <Typography
            lineHeight="14px"
            fontWeight={700}
            fontSize={10}
            color={COLORS['brown/700']}
            sx={{ minWidth: '6px' }}
          >
            {count}
          </Typography>
        </StyledCountData>
      )}
    </ControlLabelWrapper>
  );

  const renderContent = () => {
    const orderedItems = order?.length
      ? order.flatMap((group) =>
          group
            .map((code) => items.find((i) => i.code === code))
            .filter((i): i is Category | Equipment | OfferTypeObject => Boolean(i)),
        )
      : items;

    if (hideCount) {
      return (
        <Box>
          {orderedItems.map((item) => renderCheckbox(item as { title: string; count: number }))}
        </Box>
      );
    }

    const nonZeroItems = orderedItems.filter((i) => (i.count ?? 0) > 0);
    const zeroItems = orderedItems.filter((i) => (i.count ?? 0) === 0);

    const groupedNonZero = order?.length
      ? order
          .map((group) =>
            group
              .map((code) => nonZeroItems.find((i) => i.code === code))
              .filter((i): i is Category | Equipment | OfferTypeObject => Boolean(i)),
          )
          .filter((group) => group.length > 0)
      : [nonZeroItems];

    const groupedZero = order?.length
      ? order
          .map((group) =>
            group
              .map((code) => zeroItems.find((i) => i.code === code))
              .filter((i): i is Category | Equipment | OfferTypeObject => Boolean(i)),
          )
          .filter((group) => group.length > 0)
      : [zeroItems];

    return (
      <>
        {groupedNonZero.map((group, groupIndex) => (
          <Box key={`nonzero-${groupIndex}`} sx={{ '&:not(:first-of-type)': { mt: 2 } }}>
            {group.map(renderCheckbox)}
          </Box>
        ))}

        {zeroItems.length > 0 && (
          <>
            {nonZeroItems.length > 0 && <Divider sx={{ my: 1 }} />}

            {!showZeroGroups[0] ? (
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowZeroGroups({ 0: true });
                }}
                sx={{
                  fontSize: '12px',
                  color: COLORS['primary/600'],
                  height: 'fit-content',
                  width: '100%',
                  textAlign: 'center',
                  px: '0px',
                  textTransform: 'initial',
                }}
              >
                {t('filters.showEmpty')}
              </Button>
            ) : (
              <>
                {groupedZero.map((group, groupIndex) => (
                  <Box key={`zero-${groupIndex}`} sx={{ '&:not(:first-of-type)': { mt: 1 } }}>
                    {group.map(renderCheckbox)}
                  </Box>
                ))}
                <Button
                  size="small"
                  variant="text"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowZeroGroups({ 0: false });
                  }}
                  sx={{
                    fontSize: '12px',
                    color: COLORS['primary/600'],
                    height: 'fit-content',
                    width: '100%',
                    textAlign: 'center',
                    px: '0px',
                    textTransform: 'initial',
                  }}
                >
                  {t('filters.hideEmpty')}
                </Button>
              </>
            )}
          </>
        )}
      </>
    );
  };

  if (isSelect) {
    return (
      <FormControl size="small" sx={{ minWidth: width, maxWidth: width }}>
        {!hideLabel && <InputLabel>{label}</InputLabel>}
        <Select
          disabled={disabled}
          multiple
          displayEmpty={hideLabel}
          notched={hideLabel ? false : undefined}
          IconComponent={(props) => <ArrowDown {...props} />}
          sx={
            hideLabel && !value.length
              ? {
                  '& .MuiSelect-select': { color: COLORS['neutral/500'] },
                  '&.Mui-focused .MuiSelect-select': { color: COLORS['neutral/500'] },
                }
              : undefined
          }
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={value.map((item) => item.title)}
          label={hideLabel ? undefined : label}
          renderValue={(selected) => {
            const selectedItems = selected as string[];
            if (!selectedItems.length && hideLabel) {
              return label;
            }

            return selectedItems.join(', ');
          }}
          MenuProps={{
            disableScrollLock: true,
            MenuListProps: { sx: { py: 0 } },
            PaperProps: {
              sx: {
                maxHeight: 300,
                width: 150,
                overflowY: 'auto',
                scrollbarWidth: 'thin',
              },
            },
          }}
        >
          <Box
            sx={{ p: 1, pr: '14px' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {renderContent()}
          </Box>
        </Select>
      </FormControl>
    );
  }

  return (
    <FilterAccordion
      title={label}
      id={defaultOpen ? label : `${label}:collapsed`}
      defaultOpen={defaultOpen}
      hint={hint}
    >
      <FormControl sx={{ width: '100%' }}>{renderContent()}</FormControl>
    </FilterAccordion>
  );
};
